

import { useEffect, useState } from "react";
import { validatePassword } from "@/lib/utils/input-validator";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "next-intl";
import { translateBackendError } from "@/lib/i18n/translateBackendError";
import { completeResetPassword } from "@/lib/api/handlers/AuthenticationHandling";
import { useRateLimitCountdown } from "@/hooks/useRateLimitCountdown";
import { throwServerError } from "@/lib/api/errors/ErrorHandler";

type FormField = "password" | "confirmPassword";
type ValidationField = "length" | "uppercase" | "lowercase" | "specialChar";

const initialState = {
  password: "",
  confirmPassword: "",
};

const errorState: Record<ValidationField, boolean | undefined> = {
  length: undefined,
  uppercase: undefined,
  lowercase: undefined,
  specialChar: undefined,
};

/**
 * Custom hook for managing change password form state and behavior
 * @param token - Reset token from email link
 * @param expiresAt - Timestamp when the token expires
 */
export function useChangePasswordForm(token: string, expiresAt: number) {
  const [formData, setFormData] = useState(initialState);
  const [passwordValidation, setPasswordValidation] = useState(errorState);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const rateLimit = useRateLimitCountdown();
  const navigate = useNavigate();
  const t = useTranslations("errors");

  useEffect(() => {
    if (!rateLimit.isLimited) return;
    setFormErrors((prev) => ({
      ...prev,
      form: t("rateLimitedCountdown", { seconds: rateLimit.secondsLeft }),
    }));
  }, [rateLimit.secondsLeft, rateLimit.isLimited, t]);

  const handleInputChange = (field: FormField, value: string) => {
    setFormData((prevState) => ({
      ...prevState,
      [field]: value,
    }));

    if (field === "password") {
      setPasswordValidation(validatePassword(value));
    }

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [field]: "",
    }));
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (rateLimit.isLimited) return;

    const errors: Record<string, string> = {};

    if (formData.password !== formData.confirmPassword)
      errors.confirmPassword = t("passwordNoMatch");

    if (!formData.password) errors.password = t("passwordRequired");

    // Check if token has expired
    if (expiresAt < Date.now()) errors.password = t("otpExpired");

    if (Object.keys(errors)?.length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsLoading(true);

    try {
      const response = await completeResetPassword(token, formData.password);

      if (
        response &&
        "isServerError" in response &&
        response.isServerError === true
      ) {
        if (response.message === "rateLimited" && response.retryAfter) {
          rateLimit.start(response.retryAfter);
        }
        throwServerError(response);
      }

      window.dispatchEvent(new Event("navigationStart"));
      navigate("/auth/login?reset=success", { replace: true });
    } catch (error) {
      setFormErrors({
        form: translateBackendError(t, error instanceof Error ? error : "unknown"),
      });
    }
    setIsLoading(false);
  }

  async function prefetchRoute() {
  }

  return {
    formData,
    passwordValidation,
    formErrors,
    isLoading,
    handleInputChange,
    handleSubmit,
    prefetchRoute,
    rateLimited: rateLimit.isLimited,
  };
}
